import { motion } from "framer-motion";
import { Link } from "../lib/router";
import { ArrowRight, ChevronDown } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-b border-[#1C1C1A]/15 focus:border-[#C5A059] py-3 text-[15px] text-[#1C1C1A] placeholder:text-[#66625A]/45 outline-none transition-colors duration-300";
const labelCls =
  "block text-[10px] uppercase tracking-[0.25em] font-mono text-[#66625A] mb-1";

export const Field = ({ label, id, ...props }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label}
    </label>
    <input id={id} data-testid={id} className={inputCls} {...props} />
  </div>
);

export const TextArea = ({ label, id, rows = 4, ...props }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label}
    </label>
    <textarea id={id} data-testid={id} rows={rows} className={`${inputCls} resize-none`} {...props} />
  </div>
);

export const Select = ({ label, id, children, ...props }) => (
  <div className="relative">
    <label htmlFor={id} className={labelCls}>
      {label}
    </label>
    <select id={id} data-testid={id} className={`${inputCls} cursor-pointer pr-8`} {...props}>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute bottom-4 right-0 h-4 w-4 text-[#66625A]" />
  </div>
);

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const ChipGroup = ({ options, selected, onToggle, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map((opt) => {
      const active = selected.includes(opt);
      return (
        <button
          type="button"
          key={opt}
          data-testid={`${testId}-${slugify(opt)}`}
          onClick={() => onToggle(opt)}
          className={`border px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
            active
              ? "border-[#1C1C1A] bg-[#1C1C1A] text-[#FAF8F5]"
              : "border-[#1C1C1A]/20 text-[#66625A] hover:border-[#1C1C1A]"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const base =
  "group inline-flex items-center justify-center gap-3 px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium transition-colors duration-500";
const variants = {
  solid: "bg-[#1C1C1A] text-[#FAF8F5] hover:bg-[#C5A059] hover:text-[#1C1C1A]",
  outline:
    "border border-[#1C1C1A]/30 text-[#1C1C1A] hover:bg-[#1C1C1A] hover:text-[#FAF8F5]",
  light: "bg-[#FAF8F5] text-[#1C1C1A] hover:bg-[#C5A059]",
  outlineLight:
    "border border-[#FAF8F5]/40 text-[#FAF8F5] hover:bg-[#FAF8F5] hover:text-[#1C1C1A]",
};

export const PButton = ({
  to,
  children,
  variant = "solid",
  arrow = true,
  className = "",
  ...props
}) => {
  const cls = `${base} ${variants[variant]} ${className}`;
  const inner = (
    <>
      {children}
      {arrow && (
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1.5" />
      )}
    </>
  );
  return (
    <motion.span className="inline-block" whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
      {to ? (
        <Link to={to} className={cls} {...props}>
          {inner}
        </Link>
      ) : (
        <button className={cls} {...props}>
          {inner}
        </button>
      )}
    </motion.span>
  );
};
